// leadPipeline.js — pure logic for determining which pipeline stage a
// client/lead is currently sitting in, how long they've been there, and
// what severity (green/amber/red) the aging clock should show.
//
// This module is the single source of truth for the Dashboard queues.
//
// ── 2026-08-07 REWORK (Jaylen: "trim the fat") ─────────────────────────
// The original six stages assumed funnel junctions that never happen in
// practice. Two died of it: "untouched" drained only when someone clicked
// Mark First Contact (nobody did — creating the client file IS first
// contact, you're on the phone as you type), and "quoted_waiting" filled
// only when someone stamped quote_given_at (skipped — users go walkthrough
// → straight to booked). RULE NOW: every stage fills AND drains on a real
// event that happens anyway — create file, schedule walkthrough, complete
// walkthrough, create estimate, book, move date. Nothing depends on a
// bookkeeping click. first_contact_at is dead (column remains, unused).
//
// Five stages, evaluated in priority order (first match wins — latest
// point in the funnel, i.e. what they're "waiting on next"):
//   near_move_date   → within 7 days of moving, not completed. Red, always.
//   awaiting_booking → estimate exists (not draft), not booked. The sales
//                      follow-up list — no "quoted" stamp needed; the
//                      straight-to-booked flow just passes through.
//   walkthrough_review → walkthrough completed, no estimate yet.
//   awaiting_walkthrough → walkthrough scheduled, not yet completed.
//   needs_followup   → file exists, nothing scheduled at all.

import { currentMoveFor, OPTION_TUCKED } from './moves';

export const STAGE_NEEDS_FOLLOWUP    = 'needs_followup';
export const STAGE_AWAITING_WT       = 'awaiting_walkthrough';
export const STAGE_WT_REVIEW         = 'walkthrough_review';
export const STAGE_AWAITING_BOOKING  = 'awaiting_booking';
export const STAGE_NEAR_MOVE         = 'near_move_date';

// Stage display config (label, age thresholds in HOURS, what timestamp
// the aging clock starts from).
//   • Needs follow-up: we talked (file exists) — schedule something within
//     a day, stale at 3 days.
//   • Walkthrough review: brothers' 48h target, sometimes 72h when busy.
//   • Awaiting booking: estimates usually close within a couple days of
//     being built; nudge at 2 days, alarm at 5.
//   • Near move date: any lead inside 7 days of move_date that hasn't
//     booked overrides as critical regardless of stage age.
export const STAGE_CONFIG = {
    [STAGE_NEEDS_FOLLOWUP]:   { label: 'FOLLOW UP',   sinceField: 'client.created_at',               greenMax: 24, amberMax: 72 },
    [STAGE_AWAITING_WT]:      { label: 'WALKTHROUGH', sinceField: 'client.walkthrough_date',         greenMax: null, amberMax: null }, // special-cased: relative to scheduled date
    [STAGE_WT_REVIEW]:        { label: 'REVIEW',      sinceField: 'client.walkthrough_completed_at', greenMax: 24, amberMax: 48 },
    [STAGE_AWAITING_BOOKING]: { label: 'BOOK',        sinceField: 'estimate.created_at',             greenMax: 48, amberMax: 120 },
    [STAGE_NEAR_MOVE]:        { label: 'MOVE SOON',   sinceField: null,                              greenMax: null, amberMax: null },
};

// Severity → palette
const SEVERITY_PALETTE = {
    green: { bg: '#10b981', text: '#ffffff' }, // emerald-500
    amber: { bg: '#f59e0b', text: '#ffffff' }, // amber-500
    red:   { bg: '#dc2626', text: '#ffffff' }, // red-600
};

// Pick the estimate that represents a client (shared with ClientsPage).
// With a current move (2026-09-18, options): the move's booked option,
// else its newest live option, else its newest option of any status —
// "newest row" alone is wrong after a booking, when the newest sibling is
// the auto-declined one. Without a move: newest of the client's estimates.
export function pickPrimaryEstimate(estimates, clientId, move = null) {
    const byNewest = (a, b) => (b.created_at || '').localeCompare(a.created_at || '');
    const all = (estimates || []).filter(e => e.client_id === clientId);
    const mine = move ? all.filter(e => e.move_id === move.id) : all;
    // A current move with no options yet reads as "no estimate" — showing
    // the previous move's (declined / booked) option would describe the
    // wrong job.
    return mine.find(e => e.status === 'booked')
        || mine.filter(e => !OPTION_TUCKED.has(e.status)).sort(byNewest)[0]
        || mine.sort(byNewest)[0]
        || null;
}

// Pick the most recent job for a client.
function pickPrimaryJob(jobs, clientId) {
    return (jobs || [])
        .filter(j => j.client_id === clientId)
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null;
}

// Main predicate. Returns { stage, since } or null if the client has no
// active pipeline state (Booked far-out, Inactive, Cancelled, Completed —
// the fast path walkthrough → straight-to-booked drains every queue
// instantly and silently, per Jaylen 2026-08-07).
// opts.moves (2026-09-18, moves-architecture.md rule 1): when the caller
// passes the store's `moves`, the move date comes from the client's CURRENT
// move (load_date) instead of the client.move_date mirror column. The mirror
// stays correct through the transition, so callers that don't pass it get
// the same answer today; passing it is what keeps this right once the
// mirror columns go (stage 3). The returned object carries `move` (or null).
export function getClientStage(client, estimates, jobs, opts = {}) {
    if (!client) return null;
    // Inactive = dead lead (incl. cancelled walkthroughs, Jaylen
    // 2026-06-09) — drops out of every queue immediately.
    if (client.status === 'inactive') return null;
    const move = Array.isArray(opts.moves) ? currentMoveFor(client, opts.moves) : null;
    const primaryEst = opts.primaryEst !== undefined ? opts.primaryEst : pickPrimaryEstimate(estimates, client.id, move);
    const primaryJob = opts.primaryJob !== undefined ? opts.primaryJob : pickPrimaryJob(jobs, client.id);
    const status = primaryEst?.status;
    const moveDate = move ? (move.load_date || null) : client.move_date;
    const moveTentative = move ? !!move.load_tentative : !!client.date_tentative;

    // Cancelled — terminal failure state, no chip. ('lost' was retired
    // in the May 2026 status rewrite — it folded into 'cancelled'.)
    if (status === 'cancelled') return null;
    // Postponed (2026-09-21) — parked on purpose, no date: not a follow-up
    // and not near any move. It comes back when the status does.
    if (status === 'postponed') return null;

    // Completed jobs — operational lifecycle is done, no chip needed.
    if (primaryJob?.status === 'completed') return null;

    // 1. Near move date — promoted to red regardless of underlying stage
    //    if the move is within 7 days. This check runs BEFORE the booked
    //    bailout below so that Booked-but-imminent moves (e.g. UNASSIGNED
    //    job moving TODAY) still surface as critical.
    if (moveDate) {
        const moveTs = new Date(moveDate).getTime();
        const daysAway = (moveTs - Date.now()) / (1000 * 60 * 60 * 24);
        if (daysAway >= 0 && daysAway <= 7) {
            return { stage: STAGE_NEAR_MOVE, since: null, daysToMove: Math.floor(daysAway), moveDate, moveTentative, move };
        }
    }

    // 2. Booked but the move date is far enough out that the chip would
    //    just be noise. Operations side owns the job from here.
    if (status === 'booked') return null;

    // 3. Awaiting booking — an estimate exists (any working status except
    //    drafts), the client hasn't booked. Ages from estimate creation.
    //    This IS the follow-up-on-quote list; no quote_given_at stamp
    //    required (that junction never happened in practice).
    if (primaryEst && !primaryEst.is_draft) {
        return { stage: STAGE_AWAITING_BOOKING, since: primaryEst.created_at };
    }

    // Explicit completion (sql/064, 2026-07-01): a walkthrough is done when the
    // ESTIMATOR taps "Complete Walkthrough" (walkthrough_status='completed'),
    // not on a timer.
    const wtCompleted = client.walkthrough_status === 'completed';
    const wtDoneAt = wtCompleted
        ? (client.walkthrough_completed_at || client.walkthrough_date)
        : null;

    // 4. Walkthrough review — estimator marked complete, no estimate yet.
    if (wtDoneAt) {
        return { stage: STAGE_WT_REVIEW, since: wtDoneAt };
    }

    // 5. Awaiting walkthrough — scheduled, not yet marked complete.
    if (client.walkthrough_date && !wtCompleted) {
        return { stage: STAGE_AWAITING_WT, since: client.walkthrough_date };
    }

    // 6. Needs follow-up — a file exists (so we've talked: the file is
    //    born from the first call) but nothing is scheduled yet. Drains
    //    the moment a walkthrough is scheduled / an estimate exists /
    //    the client books or goes inactive.
    return { stage: STAGE_NEEDS_FOLLOWUP, since: client.created_at };
}

// Compute the chip's display info — label, age string, color severity —
// from a stageInfo object plus the client (needed for some date math).
export function getStageDisplay(stageInfo, client) {
    if (!stageInfo) return null;
    const { stage, since } = stageInfo;
    const cfg = STAGE_CONFIG[stage];
    if (!cfg) return null;

    // Special cases that don't use the hours-based threshold model
    if (stage === STAGE_AWAITING_WT) {
        // Aging is relative to the SCHEDULED walkthrough date, not a fixed
        // creation timestamp. Color reflects proximity:
        //   future > 1d: green (plenty of time)
        //   future ≤1d: amber (today or tomorrow)
        //   past: red (overdue — walkthrough should have happened)
        if (!since) return null;
        const ms = new Date(since).getTime() - Date.now();
        const days = ms / (1000 * 60 * 60 * 24);
        let severity = 'green';
        let ageText;
        if (days < 0) {
            severity = 'red';
            ageText = `OVERDUE ${Math.abs(Math.floor(days))}D`;
        } else if (days < 1) {
            severity = 'amber';
            ageText = days < 0.1 ? 'TODAY' : 'TODAY';
        } else if (days < 2) {
            severity = 'amber';
            ageText = `${Math.floor(days)}D`;
        } else {
            severity = 'green';
            ageText = `${Math.floor(days)}D`;
        }
        return {
            stage, label: cfg.label, ageText,
            severity,
            ...SEVERITY_PALETTE[severity],
        };
    }

    if (stage === STAGE_NEAR_MOVE) {
        // Always red — move date is the deadline.
        const days = stageInfo.daysToMove ?? 0;
        return {
            stage, label: cfg.label,
            ageText: days === 0 ? 'TODAY' : `${days}D`,
            severity: 'red',
            ...SEVERITY_PALETTE.red,
        };
    }

    // Standard hours-based aging
    if (!since) return null;
    const ms = Date.now() - new Date(since).getTime();
    const hours = ms / (1000 * 60 * 60);
    const days = hours / 24;

    let severity = 'green';
    if (cfg.amberMax != null && hours > cfg.amberMax) severity = 'red';
    else if (cfg.greenMax != null && hours > cfg.greenMax) severity = 'amber';

    let ageText;
    if (hours < 1) ageText = '<1H';
    else if (hours < 24) ageText = `${Math.floor(hours)}H`;
    else ageText = `${Math.floor(days)}D`;

    return {
        stage, label: cfg.label, ageText,
        severity,
        ...SEVERITY_PALETTE[severity],
    };
}

// Convenience — one-shot from client to display info (skips intermediate).
export function getClientChipDisplay(client, estimates, jobs, opts = {}) {
    const stage = getClientStage(client, estimates, jobs, opts);
    if (!stage) return null;
    return getStageDisplay(stage, client);
}
